import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { BodyText1Css, BodyText2Css } from '@panel/commons/lib/typography';
import { getBlueColors, getNeutralColors, getOrangeColors } from '@panel/commons/lib/theme';
import { Icon } from '@panel/commons/lib/Icon/Icon';
import LeadCounterContainer from '../../../leadCounter';
import MessageCounter from '../../../messageActivityCounter';

const styledItemContainer = css`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${getNeutralColors('black')};
  padding: 11px 24px 11px 32px;
  text-decoration: none;
  position: relative;
  &div {
    align-items: center;
  }
  ${({ site }) => css`
    :focus {
      color: ${site === 'CMBR' ? getBlueColors('blueCmineira') : getOrangeColors('base')};
      &::after {
        height: 40px;
        position: absolute;
        content: '';
      }
    }
    :hover {
      color: ${site === 'CMBR' ? getBlueColors('blueCmineira') : getOrangeColors('base')};
      cursor: pointer;
    }
  `}
`;

const ItemContainer = styled(NavLink)`
  ${styledItemContainer}
  ${({ border }) =>
    border &&
    css`
      border-bottom: 1px solid ${getNeutralColors('grisClaroII')};
      width: 100%;
    `}
`;

const ItemContainerAnchor = styled.a`
  ${styledItemContainer}
  &:first-child {
    border-top: 1px solid ${getNeutralColors('grisClaroII')};
    padding-top: 12px;
    width: 100%;
  }

  ${({ border }) =>
    border &&
    css`
      border-bottom: 1px solid ${getNeutralColors('grisClaroII')};
      width: 100%;
    `}
`;

const Subtitle = styled.span`
  ${BodyText2Css}
  color: ${getNeutralColors('grisMedioII')};
  display: block;
  padding: 5px 24px 5px 32px;

  ${({ border }) =>
    border &&
    css`
      border-top: 1px solid ${getNeutralColors('grisClaroII')};
      margin-top: 5px;
      padding-top: 12px;
    `}
`;

const ItemName = styled.span`
  display: block;
  font-size: 16px;
`;

const Pill = styled.span`
  ${BodyText1Css};
  border-radius: 2px;
  color: ${getNeutralColors('white')};
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  margin-left: 8px;
  padding: 0px 3px;
  text-transform: uppercase;

  ${({ color, site }) =>
    color === 'gray'
      ? css`
          background: ${getNeutralColors('grisOscuro')};
        `
      : css`
          background: ${site === 'CMBR' ? getBlueColors('blueCmineira') : getOrangeColors('base')};
        `}
`;

function HeaderMenuItem({ item, user, onMenuItemClick, border, publisherData }) {
  const { t } = useTranslation();
  const siteId = process.env.REACT_APP_BUILD_SITE;

  const handleClick = () => {
    onMenuItemClick();
  };

  const itemClickDl = () => {
    window.dataLayer.push({
      event: 'trackPageview',
      pageTitle: `${t(item.itemName)}`,
      pageName: `${t(item.path)}`
    });
  };

  const itemBranch = item.icon === 'realEstateBranch' ? 'realEstate' : null;

  return (
    <>
      {item.subtitle && <Subtitle key={item.icon}>{t(item.itemName)}</Subtitle>}
      {!item.subtitle &&
        (item.external === false ? (
          <ItemContainer
            data-panel={`headerMenuItem_${t(item.icon)}`}
            to={t(item.path)}
            onClick={handleClick}
            onMouseDown={itemClickDl}
            // Rebuscado, pero react-router-dom pasa los boolean como string
            border={border ? 1 : 0}
            site={siteId}
          >
            <Icon name={item.icon} margin='0 8px 0 0' />
            <ItemName>{t(publisherData || item.itemName)}</ItemName>
            {item.icon === 'interested' &&
              !user.isSeeker &&
              user.publisherTypeId !== '1' &&
              user.publisherTypeId !== undefined && <LeadCounterContainer isMobile site={siteId} />}
            {item.icon === 'activity' && <MessageCounter isMobile />}
            {item.pill && (
              <Pill color={item.pill.color} site={siteId}>
                {t(item.pill.text)}
              </Pill>
            )}
          </ItemContainer>
        ) : (
          <ItemContainerAnchor
            data-panel={`headerMenuItem_${t(itemBranch || item.icon)}`}
            href={`${t(item.path)}${user.userTypeId !== '1' && itemBranch ? user.publisherId : ''}`}
            rel='noopener noreferrer'
            onClick={handleClick}
            border={border ? 1 : 0}
            site={siteId}
          >
            <Icon name={itemBranch || item.icon} margin='0 8px 0 0' />
            <ItemName>{t(publisherData || item.itemName)}</ItemName>
          </ItemContainerAnchor>
        ))}
    </>
  );
}

HeaderMenuItem.propTypes = {
  item: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    itemName: PropTypes.string.isRequired
  }).isRequired
};

export { HeaderMenuItem };
